/* Mont Alpi ISLAND PDP — functional "Complete your outdoor kitchen" block.
 *
 * Ports the configurator's Gift & Accessories experience onto the standalone island PDP
 * WITHOUT touching the configurator. It reuses the same building blocks:
 *   - the same resolver module (dynamic import) for accessories() + resolveCover()
 *   - the same gift program (gift-config JSON emitted from shop.metafields.gift_program.*)
 *   - the same /cart/add.js multi-line add-to-cart path
 * The island itself is THIS product's variant (data-island-variant-id). The block hides
 * itself on any product whose SKU isn't a known ACTIVE island (covers/accessories/built-ins).
 *
 * Mirrors mont-alpi-configurator.js: composeOrder()/renderGift()/renderAccessories()/addToCart().
 */
(function () {
  const root = document.querySelector("[data-ma-shop]");
  if (!root) return;

  const $ = (s) => root.querySelector(s);
  const giftEl = $("[data-ma-gift]");
  const giftOptsEl = $("[data-ma-gift-opts]");
  const giftTitleEl = $("[data-ma-gift-title]");
  const accEl = $("[data-ma-accessories]");
  const totalEl = $("[data-ma-total]");
  const msgEl = $("[data-ma-msg]");

  // Mirrors the configurator: gift-eligible SKUs that still appear in the paid list.
  const ALWAYS_PURCHASABLE_GIFT_SKUS = new Set(["MAPZ-SS", "MASHP"]);

  const usd = (v) => { const n = Number(v); return Number.isFinite(n) ? "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 }) : ""; };
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  let giftConfig = { enabled: false, freeCount: 1, gifts: [] };
  try {
    const cfgEl = root.querySelector("[data-ma-shop-giftconfig]") || document.querySelector("[data-ma-shop-giftconfig]");
    if (cfgEl) { const p = JSON.parse(cfgEl.textContent); if (p && typeof p === "object") giftConfig = p; }
  } catch (e) { /* leave disabled */ }

  // selection state (qty keyed by accessory SKU and "__cover"; gift is always qty 1)
  const state = { gift: null, accessories: new Set(), cover: false, qty: {} };
  const qtyOf = (key) => { const q = parseInt(state.qty[key], 10); return Number.isFinite(q) && q > 0 ? q : 1; };

  // resolved-once data for this island
  let resolver, finish, sel, accList = [], cover = null, gifts = [];
  // the buy-box form + native ATC we route through (set in bindTopForm); single button.
  let topForm = null, topBtn = null, topLabel = null, adding = false;
  const mode = root.dataset.maMode || "island";
  const mainVariantId = root.dataset.maVariantId;
  const mainSku = root.dataset.maSku;
  const cleanTitle = (t) => String(t || "").replace(/^Mont Alpi\s+/i, "").replace(/\s*[-–]\s*[A-Za-z0-9\/-]+$/, "").trim();

  // Reusable accessory info tooltip — the SAME "i" trigger + rich card the Mont Alpi configurator
  // uses (.ma-tip-trigger / .ma-tip, styled in mont-alpi-content.css). Opt-in per brand via
  // data-ma-tips="1" so it activates where wired (Le Griddle first) without touching others.
  const tipsEnabled = root.dataset.maTips === "1";
  const tipMarkup = (image, title, text) => (tipsEnabled && text)
    ? `<span class="ma-tip-trigger" data-tip role="button" tabindex="0" aria-label="More information">i</span>` +
      `<span class="ma-tip" role="tooltip">${image ? `<img class="ma-tip__img" loading="lazy" src="${esc(image)}" alt="">` : ""}` +
      `<span class="ma-tip__text">${title ? `<strong>${esc(title)}</strong>` : ""}<span>${esc(text)}</span></span></span>`
    : "";

  function showMsg(text, isError) {
    if (!msgEl) return;
    msgEl.hidden = !text; msgEl.textContent = text || "";
    msgEl.classList.toggle("is-error", !!isError);
  }

  async function init() {
    // The catalog (Mont Alpi or TEC) is always needed; the resolver is island-only.
    let catalog;
    try { catalog = await (await fetch(root.dataset.catalogUrl)).json(); }
    catch (e) { return; } // can't load catalog → leave block hidden

    // GRILL mode (957/built-in grills, or TEC grills): fixed accessory set, no gift, no cover.
    // Brand-neutral — driven entirely by the catalog's accessoryProducts; no resolver needed.
    if (mode === "grill") {
      buildListsGrill(catalog);
      if (!accList.length) return; // nothing to upsell → stay hidden
      bindTopForm();              // consolidate the buy-box ATC through our multi-line add
      renderGift();               // gifts=[] → gift block stays hidden
      renderAccessories();
      bind();
      recompute();
      root.hidden = false;
      return;
    }

    // ISLAND mode needs the resolver (Mont Alpi).
    let mod;
    try { mod = await import(root.dataset.resolverUrl); resolver = mod.createResolver(catalog); }
    catch (e) { return; }
    const island = catalog.products.find((p) => p.sku === mainSku && p.status === "ACTIVE");
    if (!island) { revealCrosslink(); return; }      // module/accessory → show the cross-link instead
    const d = mod.decodeSku(mainSku);
    if (!d || !d.finish || !d.size) { revealCrosslink(); return; }
    finish = d.finish;
    sel = { finish: d.finish, size: d.size, shape: d.shape || "Straight", modules: d.modules || [] };

    // Consolidate to ONE button on every island, even if there were no add-ons (adds island only).
    bindTopForm();

    buildLists();
    if (!gifts.length && !accList.length && !cover) { updateCta(); return; }

    renderGift();
    renderAccessories();
    bind();
    recompute();
    root.hidden = false; // reveal only now that it's a valid island with options
  }

  // GRILL accessory set: a fixed SKU list (param), looked up in the catalog's module/accessory
  // products. No cover, no gift. label = cleaned product title.
  function buildListsGrill(catalog) {
    const pool = Object.assign({}, catalog.moduleProducts, catalog.accessoryProducts);
    const skus = (root.dataset.maAccessorySkus || "").split(",").map((s) => s.trim()).filter(Boolean);
    let list = skus.map((s) => pool[s]).filter((p) => p && p.status === "ACTIVE" && p.price != null && p.variantId)
      .map((p) => ({ sku: p.sku, label: cleanTitle(p.title), blurb: p.description || "", price: Number(p.price), image: p.heroImage, variantId: p.variantId }));
    // Order: build-matched cover(s) FIRST (most logical add, mid-priced), then the rest ASCENDING
    // by price (never lead with the most expensive). Cover SKUs passed via data-ma-cover-skus.
    const coverSkus = (root.dataset.maCoverSkus || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (coverSkus.length) {
      const covers = coverSkus.map((s) => list.find((a) => a.sku === s)).filter(Boolean);
      const rest = list.filter((a) => !coverSkus.includes(a.sku)).sort((x, y) => x.price - y.price);
      list = [...covers, ...rest];
    }
    accList = list;
    cover = null;
    gifts = [];
  }

  // Non-island module/accessory in the island-routing branch → reveal the cross-link callout
  // (rendered hidden alongside this block) instead of the shop UI.
  function revealCrosslink() {
    const c = document.querySelector("[data-ma-crosslink]");
    if (c) c.hidden = false;
  }

  function buildLists() {
    const all = resolver.accessories(finish);
    const giftSkus = new Set(giftConfig.enabled && Array.isArray(giftConfig.gifts) ? giftConfig.gifts.map((g) => g && g.sku).filter(Boolean) : []);
    // Paid accessories: available, priced, not series-suppressed; gift SKUs carved out
    // unless they're always-purchasable (mirrors the configurator's renderAccessories()).
    accList = all.filter((a) => a && !a.suppressed && a.available && a.price != null &&
      (!giftSkus.has(a.sku) || ALWAYS_PURCHASABLE_GIFT_SKUS.has(a.sku)));
    // Matching cover for this exact island (suppressed/bundled on Deluxe & Black).
    const cv = resolver.resolveCover(sel);
    cover = (cv && !cv.suppressed && cv.available && cv.price != null) ? cv : null;
    // Free gift: enabled, not already bundled into this series (suppressed accessory SKUs).
    const suppressed = new Set(all.filter((a) => a && a.suppressed).map((a) => a.sku));
    gifts = (giftConfig.enabled && Array.isArray(giftConfig.gifts) ? giftConfig.gifts : [])
      .filter((g) => g && g.sku && !suppressed.has(g.sku));
  }

  const giftFreeCount = Number(giftConfig.freeCount) > 0 ? Number(giftConfig.freeCount) : 1;

  function renderGift() {
    if (!gifts.length) { giftEl.hidden = true; return; }
    giftEl.hidden = false;
    giftTitleEl.textContent = gifts.length > 1 ? `Choose your free gift — pick ${giftFreeCount}` : "Your free gift, on us";
    giftOptsEl.innerHTML = gifts.map((g) => {
      const on = state.gift === g.sku;
      const was = g.strike != null && g.strike !== "" ? `<span class="ma-gift__was">${usd(g.strike)}</span>` : "";
      return `<button type="button" class="ma-gift__opt" data-giftsku="${esc(g.sku)}" role="radio" aria-checked="${on}">
        <span class="ma-gift__radio" aria-hidden="true"></span>
        <span class="ma-gift__thumb">${g.image ? `<img loading="lazy" src="${esc(g.image)}" alt="">` : ""}</span>
        <span class="ma-gift__info"><span class="ma-gift__name">${esc(g.title || g.sku)}</span>
          <span class="ma-gift__price"><span class="ma-gift__free">FREE</span>${was}</span></span></button>`;
    }).join("");
  }

  // A selectable row: a toggle <button> plus a qty stepper that appears once included.
  // key = accessory SKU, or "__cover" for the cover row. Stepper omitted on the gift block.
  function row(key, isCover, on, image, label, sub, price, tipText) {
    const q = qtyOf(key);
    return `<div class="ma-mod-row" ${isCover ? 'data-cover="1"' : `data-accsku="${esc(key)}"`}>
      <button type="button" class="ma-mod ma-acc${isCover ? " ma-acc--cover" : ""}" data-toggle aria-pressed="${on}">
        <span class="ma-mod__thumb">${image ? `<img loading="lazy" src="${esc(image)}" alt="">` : ""}</span>
        <span class="ma-mod__body"><span class="ma-mod__label">${esc(label)}${tipMarkup(image, label, tipText)}</span><span class="ma-mod__sub">${esc(sub || "")}</span></span>
        <span class="ma-mod__price">+${usd(price)}</span><span class="ma-mod__box" aria-hidden="true">✓</span>
      </button>
      <div class="ma-qty" data-qty ${on ? "" : "hidden"}>
        <button type="button" class="ma-qty__btn" data-qd="dec" aria-label="Decrease quantity">−</button>
        <span class="ma-qty__val" data-qtyval>${q}</span>
        <button type="button" class="ma-qty__btn" data-qd="inc" aria-label="Increase quantity">+</button>
      </div>
    </div>`;
  }

  function renderAccessories() {
    const coverTile = cover ? row("__cover", true, state.cover, cover.image, "Custom-Fit Island Cover",
      `Weatherproof cover matched to this island${cover.sku ? ` · ${cover.sku}` : ""}`, cover.price) : "";
    accEl.innerHTML = coverTile + accList.map((a) =>
      // tips on → blurb goes in the info card (sub stays clean); tips off → keep blurb inline as the sub (legacy)
      row(a.sku, false, state.accessories.has(a.sku), a.image, a.label, tipsEnabled ? "" : (a.blurb || ""), a.price, a.blurb || "")).join("");
  }

  function bind() {
    giftOptsEl.addEventListener("click", (e) => {
      const b = e.target.closest(".ma-gift__opt"); if (!b) return;
      const sku = b.dataset.giftsku;
      state.gift = state.gift === sku ? null : sku; // pick-1, tap again to clear
      giftOptsEl.querySelectorAll(".ma-gift__opt").forEach((x) => x.setAttribute("aria-checked", String(x.dataset.giftsku === state.gift)));
      updateCta(); // gift toggles the ATC label even though it's $0
    });
    accEl.addEventListener("click", (e) => {
      // info tooltip: tap the "i" to toggle the card (desktop also uses CSS :hover/:focus);
      // taps inside the open card do nothing — neither path toggles the row into the cart.
      const trig = e.target.closest(".ma-tip-trigger");
      if (trig) { e.preventDefault(); const open = trig.classList.contains("is-tip-open");
        accEl.querySelectorAll(".ma-tip-trigger.is-tip-open").forEach((t) => t.classList.remove("is-tip-open"));
        if (!open) trig.classList.add("is-tip-open"); return; }
      if (e.target.closest(".ma-tip")) return;
      const rowEl = e.target.closest(".ma-mod-row"); if (!rowEl) return;
      const isCover = rowEl.dataset.cover === "1";
      const key = isCover ? "__cover" : rowEl.dataset.accsku;
      // Quantity stepper (only present once the row is included)
      const qbtn = e.target.closest(".ma-qty__btn");
      if (qbtn) {
        let q = qtyOf(key);
        q = qbtn.dataset.qd === "inc" ? q + 1 : Math.max(1, q - 1); // min 1
        state.qty[key] = q;
        const v = rowEl.querySelector("[data-qtyval]"); if (v) v.textContent = q;
        recompute();
        return;
      }
      // Toggle include/exclude
      const toggle = e.target.closest("[data-toggle]"); if (!toggle) return;
      let on;
      if (isCover) { state.cover = !state.cover; on = state.cover; }
      else { if (state.accessories.has(key)) state.accessories.delete(key); else state.accessories.add(key); on = state.accessories.has(key); }
      toggle.setAttribute("aria-pressed", String(on));
      if (on && !state.qty[key]) state.qty[key] = 1; // default qty on first include
      const qEl = rowEl.querySelector("[data-qty]"); if (qEl) qEl.hidden = !on;
      recompute();
    });
    if (tipsEnabled) document.addEventListener("click", (e) => {
      if (e.target.closest(".ma-tip-trigger") || e.target.closest(".ma-tip")) return;
      accEl.querySelectorAll(".ma-tip-trigger.is-tip-open").forEach((t) => t.classList.remove("is-tip-open"));
    });
  }

  // Island quantity from the buy-box selector (select 1–9 / "10+" → number input). Independent
  // of accessory quantities; defaults to 1.
  function islandQty() {
    const selVal = topForm && topForm.querySelector("[data-quantity-select]");
    const inpVal = topForm && topForm.querySelector("[data-quantity-input]");
    let q = 1;
    if (selVal && selVal.value && selVal.value !== "10+") q = parseInt(selVal.value, 10);
    else if (inpVal && inpVal.value) q = parseInt(inpVal.value, 10);
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  // Route the buy box's native ATC through our multi-line add (island-scoped: only bound here,
  // after the island gate). Capture-phase on both click and submit so we run before Empire's
  // handler and stop it — one button, our behavior, top of the buy box.
  function bindTopForm() {
    topForm = document.querySelector("[data-product-form]");
    topBtn = topForm ? topForm.querySelector("[data-product-atc]") : null;
    topLabel = topBtn ? topBtn.querySelector(".atc-button--text") : null;
    if (!topForm || !topBtn) return;
    const handler = (e) => { e.preventDefault(); e.stopImmediatePropagation(); addToCart(); };
    topBtn.addEventListener("click", handler, true);
    topForm.addEventListener("submit", handler, true);
    updateCta();
  }

  // Running add-on total (gift is $0, never counted) — mirrors the configurator's accessoriesSubtotal.
  function addonSubtotal() {
    let t = 0;
    for (const a of accList) if (state.accessories.has(a.sku)) t += a.price * qtyOf(a.sku);
    if (state.cover && cover) t += cover.price * qtyOf("__cover");
    return t;
  }
  // Anything ticked? (≥1 accessory, the cover, or a free gift)
  function hasSelection() { return state.accessories.size > 0 || state.cover || !!state.gift; }
  // ATC label is dynamic on the SINGLE top button: "Add to cart" with nothing selected; becomes
  // "Add island + selected to cart" once an add-on or the gift is chosen.
  function updateCta() { if (topLabel) topLabel.textContent = hasSelection() ? "Add island + selected to cart" : "Add to cart"; }
  function recompute() { totalEl.textContent = usd(addonSubtotal()); updateCta(); }

  // Build the cart line set and POST /cart/add.js (same path/shape as the configurator).
  function composeItems() {
    const lines = [];
    const unresolved = [];
    // 1) main line — THIS product's variant (island or grill), at the buy-box quantity
    if (mainVariantId) lines.push({ id: Number(mainVariantId), quantity: islandQty() });
    else unresolved.push(mainSku || "product");
    // 2) ticked paid accessories — each at its own stepper quantity
    for (const a of accList) {
      if (!state.accessories.has(a.sku)) continue;
      if (a.variantId) lines.push({ id: Number(a.variantId), quantity: qtyOf(a.sku) });
      else unresolved.push(a.sku);
    }
    // 3) matching cover — at its stepper quantity
    if (state.cover && cover) {
      if (cover.variantId) lines.push({ id: Number(cover.variantId), quantity: qtyOf("__cover") });
      else unresolved.push(cover.sku || "cover");
    }
    // 4) chosen free gift — $0/free variant, HARD-CAPPED at quantity 1 (no stepper)
    if (state.gift) {
      const g = gifts.find((x) => x.sku === state.gift);
      if (g && g.variantId) lines.push({ id: Number(g.variantId), quantity: 1, properties: { Bonus: "Free gift" } });
      else if (g) unresolved.push(g.sku);
    }
    return { lines, unresolved };
  }

  async function addToCart() {
    if (adding) return; // guard against click + submit both firing
    const { lines, unresolved } = composeItems();
    if (!lines.length) { showMsg("Nothing to add yet.", true); return; }
    if (unresolved.length) { showMsg(`Couldn't resolve: ${unresolved.join(", ")}. Nothing was added.`, true); return; }
    adding = true;
    if (topBtn) topBtn.disabled = true;
    showMsg("Adding to cart…", false);
    try {
      const res = await fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: lines }) });
      if (!res.ok) { const t = await res.text(); throw new Error(`${res.status} ${t.slice(0, 160)}`); }
      showMsg("Added ✓ Taking you to your cart…", false);
      window.location.href = "/cart";
    } catch (e) {
      adding = false;
      if (topBtn) topBtn.disabled = false;
      showMsg(`Add to cart failed — ${e.message}. Nothing was added.`, true);
    }
  }

  if (document.readyState !== "loading") init();
  else document.addEventListener("DOMContentLoaded", init);
})();
